<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useBookingStore } from "@stores/BookingStore";
import { useField } from "vee-validate";
import * as yup from "yup";
import AutoComplete from "primevue/autocomplete";

const props = defineProps({
  formName: String,
  name: String,
  label: String,
  placeholder: String,
  description: String,
  data: Array,
  multiple: Boolean,
  modelValue: {
    type: [Array, Object, String],
    default: [],
  },
  showRequired: {
    type: Boolean,
    default: true,
  },
  errorPosition: {
    type: String,
    default: "static",
  },
  type: {
    type: String,
    default: "text",
  },
  required: {
    type: Boolean,
    default: false,
  },
  // Mode: 'simple' returns formatted address strings, 'autocomplete' returns full place objects
  mode: {
    type: String,
    default: "simple",
    validator: (value) => ["simple", "autocomplete"].includes(value),
  },
});

const bookingStore = useBookingStore();
const items = ref([]);

const placeholderText = computed(() =>
  Array.isArray(value.value) && value.value.length ? "" : props.placeholder
);

const { value, errorMessage, validate } = useField(
  props.name,
  yup.lazy((value) => {
    const isArray = Array.isArray(value);
    const isObject = typeof value === "object" && value !== null;

    const schema = isArray
      ? yup.array()
      : isObject
      ? yup.object()
      : yup.string();

    return props.required
      ? schema.required(`${props.label} is required.`)
      : schema;
  }),
  { initialValue: props.modelValue?.length > 0 ? props.modelValue : "" }
);

// Define emits to declare event emission capability
const emit = defineEmits(["update:modelValue", "place-selected"]);

const onSelect = (event) => {
  if (props.mode === "autocomplete") {
    const selectedPlace = event.value;
    if (selectedPlace && selectedPlace.address_components) {
      emit("place-selected", selectedPlace);
      // Extract postal code from address components
      const postalCodeComponent = selectedPlace.address_components.find(
        (component) => component.types.includes("postal_code")
      );
      if (postalCodeComponent) {
        value.value = postalCodeComponent.long_name;
      }
    }
  }
};

const onClear = () => {};

const search = async (event) => {
  if (event.query.trim() === "") {
    items.value = [];
    return;
  }

  try {
    if (props.mode === "autocomplete") {
      const response = await bookingStore.getPlacesAutocomplete(event.query.trim());
      if (response?.predictions && Array.isArray(response.predictions)) {
        items.value = response.predictions;
      } else {
        items.value = [];
      }
    } else {
      const response = await bookingStore.getPlaces(event.query.trim());
      if (Array.isArray(response)) {
        items.value = response.map((i) => i.formatted_address);
      } else {
        items.value = [response.formatted_address];
      }
    }
  } catch (error) {
    console.error("Failed to get places:", error);
    items.value = [];
  }
};

watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});
</script>

<template>
  <fieldset class="frm-field">
    <div class="mb-10" :class="{ 'flex justify-between': $slots.label }">
      <label :for="name" class="block">
        {{ label }}
        <span v-if="required && showRequired" class="text-red-500">*</span>
      </label>
      <slot name="label"></slot>
    </div>

    <div class="z-10 relative flex gap-2 px-0 frm-input">
      <AutoComplete
        class="w-full"
        v-model="value"
        :optionLabel="mode === 'autocomplete' ? 'description' : undefined"
        :id="name"
        :name="name"
        :minLength="2"
        :placeholder="placeholderText"
        :suggestions="items"
        :multiple="multiple"
        @complete="search"
        forceSelection
        @blur="validate"
        @item-select="onSelect"
        @item-unselect="onClear"
        autocomplete="off">
      </AutoComplete>
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none"
      :class="{ 'md:absolute': errorPosition === 'absolute' }">
      {{ errorMessage }}
    </span>
    <span v-if="description" class="frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>
